import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, IconButton, Stack, Tooltip, Typography } from "@mui/joy";

import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import {
  subscribeToConversationSseFrames,
  type ObservedConversationSseFrame,
} from "../../utils/idaChatStreamConfig";
import {
  createGptLiveVoiceTransport,
  type GptLiveAuthFetch,
  type GptLiveCloseReason,
  type GptLiveVoiceConfig,
  type GptLiveVoicePhase,
  type GptLiveTranscriptBoundary,
  type GptLiveTranscriptTurn,
  type GptLiveVoiceTransport,
} from "../../services/gptLiveVoiceTransport";

export type GptLiveVoiceState =
  "off" | "connecting" | GptLiveVoicePhase | "ending" | "error";

export interface GptLiveVoiceFeedback {
  message: string;
  tone: "danger" | "neutral";
}

interface GptLiveVoiceControlProps {
  workspaceId: string;
  conversationId: number | null;
  ensureConversation?: () => Promise<number | null>;
  authFetch: GptLiveAuthFetch;
  onPhaseChange?: (phase: GptLiveVoicePhase | null) => void;
  onToolComposing?: (callId: string | null) => void;
  onTranscriptTurns?: (
    turns: GptLiveTranscriptTurn[],
    boundary: GptLiveTranscriptBoundary,
  ) => void;
  onFeedbackChange?: (feedback: GptLiveVoiceFeedback | null) => void;
  transportFactory?: (config: GptLiveVoiceConfig) => GptLiveVoiceTransport;
}

const DELEGATION_TERMINAL_FAILURE_TYPES = new Set([
  "error",
  "cancelled",
  "queue_item_cancelled",
  "queue_item_failed",
]);
const MAX_EARLY_DELEGATION_TERMINALS = 20;

function rememberEarlyTerminalId(ids: Set<string>, id: string) {
  ids.delete(id);
  ids.add(id);
  while (ids.size > MAX_EARLY_DELEGATION_TERMINALS) {
    const oldestId = ids.values().next().value;
    if (oldestId === undefined) return;
    ids.delete(oldestId);
  }
}

function stopTracks(stream: MediaStream | null) {
  stream?.getTracks().forEach((track) => track.stop());
}

async function endQuietly(transport: GptLiveVoiceTransport | null) {
  try {
    await transport?.end();
  } catch {
    // Cleanup must not replace the visible connection error or unmount.
  }
}

function microphoneErrorMessage(error: unknown): string {
  if (
    error &&
    typeof error === "object" &&
    "status" in error &&
    (error as { status?: unknown }).status === 429
  ) {
    return "GPT-Live capacity is temporarily unavailable.";
  }
  if (error instanceof Error && error.name === "NotAllowedError") {
    return "Microphone access was denied.";
  }
  if (error instanceof Error && error.name === "NotFoundError") {
    return "No microphone was found.";
  }
  if (
    error instanceof Error &&
    error.message === "The GPT-Live connection is not configured yet."
  ) {
    return error.message;
  }
  return "Voice could not connect.";
}

function closeReasonMessage(reason: GptLiveCloseReason | null): string {
  const messages: Record<GptLiveCloseReason, string> = {
    close_requested: "The voice session was closed by request.",
    expired: "The voice session reached its time limit.",
    content: "The voice session ended after the conversation completed.",
    remote_hangup: "The voice provider closed the connection.",
    connection_lost: "The voice connection was lost.",
    normal_closure: "The voice session ended normally.",
    protocol_error: "The voice connection ended because of a protocol error.",
    provider_closed: "The voice provider closed the session.",
    invalid_provider_frame: "The voice provider sent an invalid event.",
    event_processing_failed: "The voice service could not process an event.",
    lease_lost: "The voice session lease expired.",
    lease_error: "The voice session lease failed.",
    client_ended: "The voice session was ended by this browser.",
    server_shutdown: "The voice session ended during server shutdown.",
    max_session_length: "Voice session reached its maximum length",
  };
  return reason ? messages[reason] : "Voice session ended.";
}

function WaveIcon({ active }: { active: boolean }) {
  return active ? (
    <span
      className="ida-gpt-live-wave ida-gpt-live-wave--active"
      data-testid="gpt-live-wave-animation"
      aria-hidden="true"
    >
      <span />
      <span />
      <span />
      <span />
      <span />
    </span>
  ) : (
    <GraphicEqIcon data-testid="gpt-live-wave-static" aria-hidden="true" />
  );
}

export function GptLiveVoiceControl({
  workspaceId,
  conversationId,
  ensureConversation,
  authFetch,
  onPhaseChange,
  onToolComposing,
  onTranscriptTurns,
  onFeedbackChange,
  transportFactory = createGptLiveVoiceTransport,
}: GptLiveVoiceControlProps) {
  const [state, setState] = useState<GptLiveVoiceState>("off");
  const [error, setError] = useState<string | null>(null);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const [reconnectAvailable, setReconnectAvailable] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const transportRef = useRef<GptLiveVoiceTransport | null>(null);
  const startGenerationRef = useRef(0);
  const stopRequestedRef = useRef(false);
  const previousConversationIdRef = useRef(conversationId);
  const activeConversationIdRef = useRef<number | null>(conversationId);
  const activeSessionIdRef = useRef<string | null>(null);
  const pendingDelegationsRef = useRef(
    new Map<
      string,
      { submissionId: string | null; assistantTurnId: string | null }
    >(),
  );
  const delegationTurnIdsByCallIdRef = useRef(new Map<string, string>());
  const delegationCallIdsByTurnIdRef = useRef(new Map<string, string>());
  const delegationTurnIdsBySubmissionIdRef = useRef(new Map<string, string>());
  const earlyDelegationTerminalSubmissionIdsRef = useRef(new Set<string>());
  const earlyDelegationTerminalTurnIdsRef = useRef(new Set<string>());

  const resetDelegationTracking = useCallback(() => {
    pendingDelegationsRef.current.clear();
    delegationTurnIdsByCallIdRef.current.clear();
    delegationCallIdsByTurnIdRef.current.clear();
    delegationTurnIdsBySubmissionIdRef.current.clear();
    earlyDelegationTerminalSubmissionIdsRef.current.clear();
    earlyDelegationTerminalTurnIdsRef.current.clear();
  }, []);

  const completeDelegatedAnswer = useCallback((callId: string) => {
    const delegation = pendingDelegationsRef.current.get(callId);
    if (!delegation) return;
    pendingDelegationsRef.current.delete(callId);
    delegationTurnIdsByCallIdRef.current.delete(callId);
    if (delegation.submissionId) {
      delegationTurnIdsBySubmissionIdRef.current.delete(
        delegation.submissionId,
      );
      earlyDelegationTerminalSubmissionIdsRef.current.delete(
        delegation.submissionId,
      );
    }
    if (delegation.assistantTurnId) {
      earlyDelegationTerminalTurnIdsRef.current.delete(
        delegation.assistantTurnId,
      );
    }
    for (const [turnId, mappedCallId] of delegationCallIdsByTurnIdRef.current) {
      if (mappedCallId === callId) {
        delegationCallIdsByTurnIdRef.current.delete(turnId);
      }
    }
    transportRef.current?.completeDelegation(callId);
  }, []);

  const handleDelegationFrame = useCallback(
    (frame: ObservedConversationSseFrame) => {
      if (
        String(frame.conversation_id ?? "") !==
        String(activeConversationIdRef.current ?? "")
      ) {
        return;
      }

      const submissionId = frame.submission_id;
      const assistantTurnId = frame.assistant_turn_id;
      const voiceCallId = frame.voice_call_id;
      if (frame.type === "assistant_turn_started" && assistantTurnId) {
        let matchedCallId =
          typeof voiceCallId === "string" ? voiceCallId : undefined;
        if (!matchedCallId && submissionId) {
          matchedCallId = [...pendingDelegationsRef.current].find(
            ([, delegation]) => delegation.submissionId === submissionId,
          )?.[0];
        }
        if (submissionId) {
          delegationTurnIdsBySubmissionIdRef.current.set(
            submissionId,
            assistantTurnId,
          );
        }
        if (matchedCallId) {
          delegationTurnIdsByCallIdRef.current.set(
            matchedCallId,
            assistantTurnId,
          );
          delegationCallIdsByTurnIdRef.current.set(
            assistantTurnId,
            matchedCallId,
          );
          const delegation = pendingDelegationsRef.current.get(matchedCallId);
          if (delegation) delegation.assistantTurnId = assistantTurnId;
          if (earlyDelegationTerminalTurnIdsRef.current.has(assistantTurnId)) {
            completeDelegatedAnswer(matchedCallId);
          }
        }
        return;
      }

      const isSuccessfulTerminal = frame.type === "done";
      const isTerminalFailure = DELEGATION_TERMINAL_FAILURE_TYPES.has(
        frame.type,
      );
      if (!isSuccessfulTerminal && !isTerminalFailure) return;

      if (pendingDelegationsRef.current.size === 0) {
        if (submissionId) {
          rememberEarlyTerminalId(
            earlyDelegationTerminalSubmissionIdsRef.current,
            submissionId,
          );
        }
        if (assistantTurnId) {
          rememberEarlyTerminalId(
            earlyDelegationTerminalTurnIdsRef.current,
            assistantTurnId,
          );
        }
        return;
      }

      const submissionCallId = submissionId
        ? [...pendingDelegationsRef.current].find(
            ([, delegation]) => delegation.submissionId === submissionId,
          )?.[0]
        : undefined;
      const turnCallId = assistantTurnId
        ? (delegationCallIdsByTurnIdRef.current.get(assistantTurnId) ??
          [...pendingDelegationsRef.current].find(
            ([, delegation]) => delegation.assistantTurnId === assistantTurnId,
          )?.[0])
        : undefined;
      const matchedCallId = submissionCallId ?? turnCallId;
      if (matchedCallId) {
        completeDelegatedAnswer(matchedCallId);
        return;
      }

      // Keep unmatched terminals for the call/turn correlation that may arrive
      // later. Provider idleness cannot prove the server-side delegated request
      // finished: submit_chat_request is accepted before its answer is ready.
      // Clearing all calls on an uncorrelated terminal could hide running work.
      if (submissionId) {
        rememberEarlyTerminalId(
          earlyDelegationTerminalSubmissionIdsRef.current,
          submissionId,
        );
      }
      if (assistantTurnId) {
        rememberEarlyTerminalId(
          earlyDelegationTerminalTurnIdsRef.current,
          assistantTurnId,
        );
      }
    },
    [completeDelegatedAnswer],
  );

  useEffect(
    () => subscribeToConversationSseFrames(handleDelegationFrame),
    [handleDelegationFrame],
  );

  const releaseMedia = useCallback(() => {
    stopTracks(streamRef.current);
    streamRef.current = null;
  }, []);

  const end = useCallback(async () => {
    if (state === "off" || state === "ending") return;
    setState("ending");
    onPhaseChange?.(null);
    onToolComposing?.(null);
    setError(null);
    setPlaybackError(null);
    setReconnectAvailable(false);
    stopRequestedRef.current = true;
    resetDelegationTracking();
    releaseMedia();
    const transport = transportRef.current;
    transportRef.current = null;
    activeConversationIdRef.current = null;
    activeSessionIdRef.current = null;
    try {
      await transport?.end();
      startGenerationRef.current += 1;
      setState("off");
    } catch {
      startGenerationRef.current += 1;
      setState("error");
      setError("Voice session did not end cleanly.");
    }
  }, [
    onPhaseChange,
    onToolComposing,
    releaseMedia,
    resetDelegationTracking,
    state,
  ]);

  const handleProviderSessionEnded = useCallback(
    (frame: ObservedConversationSseFrame) => {
      if (
        frame.type !== "voice_session_ended" ||
        (frame.reason !== "content_filter" &&
          frame.reason !== "provider_error") ||
        frame.conversation_id === undefined ||
        String(frame.conversation_id) !==
          String(activeConversationIdRef.current) ||
        !frame.session_id ||
        frame.session_id !== activeSessionIdRef.current
      ) {
        return;
      }

      const transport = transportRef.current;
      if (!transport) return;

      stopRequestedRef.current = true;
      // Flush any final client-side transcript batch before invalidating the
      // callbacks. The provider already ended, so local teardown must not POST
      // another session end request.
      const localTeardown = transport.end({ notifyServer: false });
      startGenerationRef.current += 1;
      transportRef.current = null;
      activeConversationIdRef.current = null;
      activeSessionIdRef.current = null;
      resetDelegationTracking();
      releaseMedia();
      onPhaseChange?.(null);
      onToolComposing?.(null);
      setPlaybackError(null);
      const endReason =
        frame.reason === "content_filter"
          ? "The voice provider ended the session after filtering the response."
          : "The voice provider ended the session because of an error.";
      setError(frame.notice_persisted === true ? null : endReason);
      setReconnectAvailable(true);
      setState("error");
      void localTeardown.catch(() => {
        // The provider has already ended this session; local media cleanup is complete.
      });
    },
    [onPhaseChange, onToolComposing, releaseMedia, resetDelegationTracking],
  );

  useEffect(
    () => subscribeToConversationSseFrames(handleProviderSessionEnded),
    [handleProviderSessionEnded],
  );

  useEffect(() => {
    const previousConversationId = previousConversationIdRef.current;
    previousConversationIdRef.current = conversationId;

    // Empty-chat voice creates and selects its conversation while the session
    // is connecting. Preserve that null -> id transition; switching away from
    // an already selected conversation must end the session bound to it.
    if (
      previousConversationId !== null &&
      previousConversationId !== conversationId
    ) {
      void end();
    }
  }, [conversationId, end]);

  const start = useCallback(async () => {
    if (state !== "off" && state !== "error") return;
    const generation = ++startGenerationRef.current;
    stopRequestedRef.current = false;
    setError(null);
    setPlaybackError(null);
    setReconnectAvailable(false);
    onPhaseChange?.(null);
    onToolComposing?.(null);
    activeConversationIdRef.current = null;
    activeSessionIdRef.current = null;
    setState("connecting");

    let activeConversationId = conversationId;
    if (activeConversationId === null) {
      try {
        if (!ensureConversation) {
          throw new Error("Conversation creation is unavailable.");
        }
        activeConversationId = await ensureConversation();
        if (
          generation !== startGenerationRef.current ||
          stopRequestedRef.current
        ) {
          return;
        }
        if (activeConversationId === null) {
          setState("off");
          onPhaseChange?.(null);
          return;
        }
        if (
          !Number.isSafeInteger(activeConversationId) ||
          activeConversationId <= 0
        ) {
          throw new Error("Conversation creation returned an invalid ID.");
        }
      } catch {
        if (generation !== startGenerationRef.current) return;
        setState("error");
        setError("Could not create a conversation. Try again.");
        onPhaseChange?.(null);
        return;
      }
    }

    let attemptTransport: GptLiveVoiceTransport | null = null;
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Microphone capture is not supported in this browser.");
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (
        generation !== startGenerationRef.current ||
        stopRequestedRef.current
      ) {
        stopTracks(stream);
        return;
      }
      streamRef.current = stream;
      activeConversationIdRef.current = activeConversationId;
      const transport = transportFactory({
        workspaceId,
        conversationId: activeConversationId,
        authFetch,
        requestKey: window.crypto.randomUUID(),
      });
      attemptTransport = transport;
      transportRef.current = transport;
      await transport.connect(stream, {
        onSessionCreated: (sessionId) => {
          if (
            generation === startGenerationRef.current &&
            transportRef.current === transport
          ) {
            activeSessionIdRef.current = sessionId;
          }
        },
        onTranscriptTurns: (turns, boundary) => {
          if (
            generation === startGenerationRef.current &&
            transportRef.current === transport
          ) {
            onTranscriptTurns?.(turns, boundary);
          }
        },
        onPhase: (nextPhase) => {
          if (
            generation === startGenerationRef.current &&
            transportRef.current === transport
          ) {
            if (
              nextPhase === "listening" &&
              pendingDelegationsRef.current.size > 0
            ) {
              return;
            }
            const earlyCompletedCallIds =
              nextPhase === "delegating"
                ? [...pendingDelegationsRef.current].flatMap(
                    ([callId, delegation]) =>
                      delegation.assistantTurnId &&
                      earlyDelegationTerminalTurnIdsRef.current.has(
                        delegation.assistantTurnId,
                      )
                        ? [callId]
                        : [],
                  )
                : [];
            setState(nextPhase);
            onPhaseChange?.(nextPhase);
            earlyCompletedCallIds.forEach(completeDelegatedAnswer);
          }
        },
        onToolCallStart: (callId) => {
          if (
            generation === startGenerationRef.current &&
            transportRef.current === transport
          ) {
            onToolComposing?.(callId);
          }
        },
        onToolCallComplete: (callId) => {
          if (
            generation === startGenerationRef.current &&
            transportRef.current === transport &&
            callId
          ) {
            onToolComposing?.(null);
          }
        },
        onDelegationStarted: (callId) => {
          if (
            generation === startGenerationRef.current &&
            transportRef.current === transport
          ) {
            pendingDelegationsRef.current.set(callId, {
              submissionId: null,
              assistantTurnId:
                delegationTurnIdsByCallIdRef.current.get(callId) ?? null,
            });
          }
        },
        onDelegationAccepted: (callId, submissionId) => {
          if (
            generation !== startGenerationRef.current ||
            transportRef.current !== transport
          ) {
            return;
          }
          const delegation = pendingDelegationsRef.current.get(callId);
          if (!delegation) return;
          delegation.submissionId = submissionId;
          const earlyTurnId =
            delegationTurnIdsBySubmissionIdRef.current.get(submissionId) ??
            delegation.assistantTurnId;
          if (earlyTurnId) {
            delegation.assistantTurnId = earlyTurnId;
            delegationTurnIdsByCallIdRef.current.set(callId, earlyTurnId);
            delegationCallIdsByTurnIdRef.current.set(earlyTurnId, callId);
          }
          if (
            earlyDelegationTerminalSubmissionIdsRef.current.has(submissionId) ||
            (earlyTurnId &&
              earlyDelegationTerminalTurnIdsRef.current.has(earlyTurnId))
          ) {
            completeDelegatedAnswer(callId);
          }
        },
        onDisconnect: (closeReason, reasonFinal) => {
          if (
            generation !== startGenerationRef.current ||
            stopRequestedRef.current
          ) {
            return;
          }
          if (reasonFinal) {
            setError(closeReasonMessage(closeReason));
            return;
          }
          if (transportRef.current !== transport) return;
          releaseMedia();
          transportRef.current = null;
          resetDelegationTracking();
          setState("error");
          setError("Voice session ended.");
          setReconnectAvailable(true);
          onPhaseChange?.(null);
          onToolComposing?.(null);
          const disconnectError = new Error("WebRTC peer connection was lost");
          console.error(
            disconnectError,
            `Failed to maintain GPT-Live voice session for conversation ${activeConversationId}: WebRTC peer connection was lost`,
          );
        },
        onPlaybackError: () => {
          if (generation === startGenerationRef.current) {
            setPlaybackError(
              "Voice audio could not play. Check your browser's audio permissions.",
            );
          }
        },
        onDelegationFailure: (reasonCode) => {
          if (generation !== startGenerationRef.current) return;
          resetDelegationTracking();
          setState("listening");
          setError(
            `IDA could not complete the request (reason code: ${reasonCode}).`,
          );
          onToolComposing?.(null);
          onPhaseChange?.("listening");
        },
      });
      if (
        generation === startGenerationRef.current &&
        transportRef.current === transport
      ) {
        if (pendingDelegationsRef.current.size === 0) {
          setState("listening");
          onPhaseChange?.("listening");
        }
      }
    } catch (cause) {
      if (generation !== startGenerationRef.current) return;
      if (
        attemptTransport !== null &&
        transportRef.current !== attemptTransport
      ) {
        return;
      }
      releaseMedia();
      const failedTransport = transportRef.current;
      transportRef.current = null;
      await endQuietly(failedTransport);
      if (stopRequestedRef.current) return;
      setState("error");
      onPhaseChange?.(null);
      onToolComposing?.(null);
      setError(microphoneErrorMessage(cause));
    }
  }, [
    authFetch,
    completeDelegatedAnswer,
    conversationId,
    ensureConversation,
    onPhaseChange,
    onToolComposing,
    onTranscriptTurns,
    releaseMedia,
    resetDelegationTracking,
    state,
    transportFactory,
    workspaceId,
  ]);

  useEffect(
    () => () => {
      startGenerationRef.current += 1;
      onToolComposing?.(null);
      stopTracks(streamRef.current);
      const transport = transportRef.current;
      transportRef.current = null;
      if (transport) void endQuietly(transport);
    },
    [onToolComposing],
  );

  const active = state !== "off" && state !== "error";
  const hasFeedback = Boolean(error || playbackError);
  const hasErrorFeedback = Boolean(error || playbackError);
  const feedbackMessage = error || playbackError;
  const feedbackTone = error || playbackError ? "danger" : "neutral";
  const stateLabel: Record<GptLiveVoiceState, string> = {
    off: "Voice off",
    connecting: "Connecting to voice",
    listening: "Listening",
    "live-response": "IDA is speaking",
    delegating: "IDA is working",
    ending: "Ending voice",
    error: "Voice unavailable",
  };

  useEffect(() => {
    onFeedbackChange?.(
      feedbackMessage ? { message: feedbackMessage, tone: feedbackTone } : null,
    );
  }, [feedbackMessage, feedbackTone, onFeedbackChange]);

  useEffect(() => () => onFeedbackChange?.(null), [onFeedbackChange]);

  return (
    <Stack
      className="ida-gpt-live-control"
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        position: "relative",
        flex: "0 0 112px",
        width: 112,
        minWidth: 112,
        flexWrap: "nowrap",
        "& [data-testid='gpt-live-voice-state']": {
          width: 64,
          minWidth: 64,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
      }}
      data-feedback-visible={hasFeedback ? "true" : "false"}
      data-testid="gpt-live-control"
    >
      <Tooltip
        title={
          reconnectAvailable
            ? "Reconnect voice"
            : active
              ? "End voice conversation"
              : "Start voice conversation"
        }
      >
        <IconButton
          aria-label={
            reconnectAvailable
              ? "Reconnect voice"
              : active
                ? "End voice conversation"
                : "Start voice conversation"
          }
          aria-pressed={active}
          aria-busy={state === "connecting" || state === "ending"}
          data-testid="gpt-live-wave-button"
          color={active ? "primary" : "neutral"}
          variant={active ? "solid" : "plain"}
          disabled={state === "connecting" || state === "ending"}
          onClick={() =>
            reconnectAvailable
              ? void start()
              : active
                ? void end()
                : void start()
          }
        >
          <WaveIcon active={active} />
        </IconButton>
      </Tooltip>
      {state !== "off" && !hasErrorFeedback && (
        <Typography
          level="body-xs"
          role="status"
          aria-live="polite"
          data-testid="gpt-live-voice-state"
        >
          {stateLabel[state]}
        </Typography>
      )}
      {!onFeedbackChange && error && (
        <Alert
          color="danger"
          size="sm"
          role="alert"
          sx={{
            position: "absolute",
            right: 0,
            bottom: "calc(100% + 8px)",
            width: "max-content",
            maxWidth: "min(420px, calc(100vw - 32px))",
            zIndex: 10,
          }}
        >
          {error}
        </Alert>
      )}
      {!onFeedbackChange && playbackError && (
        <Alert
          color="danger"
          size="sm"
          role="alert"
          sx={{
            position: "absolute",
            right: 0,
            bottom: "calc(100% + 8px)",
            width: "max-content",
            maxWidth: "min(420px, calc(100vw - 32px))",
            zIndex: 10,
          }}
        >
          {playbackError}
        </Alert>
      )}
    </Stack>
  );
}
